import { useEffect, useRef, useSyncExternalStore } from 'react'
import { NavigationType, useBlocker } from 'react-router-dom'
import { isBeaconLoaded, isBeaconPath, loadDocument, subscribeBeacon } from '@/lib/site-metrics'

/**
 * Keeps a document that carries the Cloudflare beacon on the addresses it may report
 * (`BEACON_PATHS` in lib/site-metrics.ts, which says why). A navigation to any other address
 * becomes a full page load, so the app starts in a fresh document without the beacon, and the
 * beacon's last send, made as the old document goes, still reads a listed address.
 *
 * Rendered by every layout a beacon document can be on (`/`, PublicLayout, AuthLayout). It blocks
 * nothing until the beacon is loaded, and registers no blocker at all before then, so the app's
 * own `UnsavedChangesGuard` stays the router's only blocker everywhere else.
 */
export function PageBeaconBoundary() {
  const loaded = useSyncExternalStore(subscribeBeacon, isBeaconLoaded, () => false)
  return loaded ? <LeaveByFullLoad /> : null
}

function LeaveByFullLoad() {
  const replace = useRef(false)
  const blocker = useBlocker(({ nextLocation, historyAction }) => {
    if (isBeaconPath(nextLocation.pathname)) return false
    replace.current = historyAction === NavigationType.Replace
    return true
  })
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    const { pathname, search, hash } = blocker.location
    loadDocument(`${pathname}${search}${hash}`, replace.current)
  }, [blocker])
  return null
}
